import { useState, useEffect } from 'react';
import { Delete } from 'lucide-react';

export function CalculatorApp() {
  const [display, setDisplay] = useState('0');
  const [acc, setAcc] = useState<number | null>(null);
  const [op, setOp] = useState<string | null>(null);
  const [fresh, setFresh] = useState(true);

  const inputDigit = (d: string) => {
    if (display === 'Error') {
      setDisplay(d);
      setFresh(false);
      return;
    }
    if (fresh) {
      setDisplay(d === '.' ? '0.' : d);
      setFresh(false);
    } else {
      if (d === '.' && display.includes('.')) return;
      if (display.replace('-', '').replace('.', '').length >= 14) return;
      setDisplay(display === '0' && d !== '.' ? d : display + d);
    }
  };

  const compute = (a: number, b: number, o: string): number => {
    switch (o) {
      case '+': return a + b;
      case '−': return a - b;
      case '×': return a * b;
      case '÷': return b === 0 ? NaN : a / b;
      case '%': return a % b;
      default: return b;
    }
  };

  const fmt = (n: number): string => {
    if (!isFinite(n) || isNaN(n)) return 'Error';
    const r = Math.round(n * 1e10) / 1e10;
    return String(r).slice(0, 15);
  };

  const applyOp = (o: string) => {
    const cur = parseFloat(display);
    if (op && !fresh && acc !== null) {
      const r = compute(acc, cur, op);
      setAcc(isNaN(r) ? null : r);
      setDisplay(fmt(r));
    } else if (!isNaN(cur)) {
      setAcc(cur);
    }
    setOp(o);
    setFresh(true);
  };

  const equals = () => {
    if (op === null || acc === null) return;
    const r = compute(acc, parseFloat(display), op);
    setDisplay(fmt(r));
    setAcc(null);
    setOp(null);
    setFresh(true);
  };

  const clear = () => {
    setDisplay('0');
    setAcc(null);
    setOp(null);
    setFresh(true);
  };

  const backspace = () => {
    if (fresh || display === 'Error') {
      setDisplay('0');
      setFresh(true);
      return;
    }
    const s = display.length > 1 ? display.slice(0, -1) : '0';
    setDisplay(s === '-' ? '0' : s);
  };

  const negate = () => {
    if (display === '0' || display === 'Error') return;
    setDisplay(display.startsWith('-') ? display.slice(1) : '-' + display);
  };

  // Physical keyboard support. Ignored while typing in inputs/editors/terminal
  // so calculator keys never hijack other apps.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const el = document.activeElement as HTMLElement | null;
      const tag = (el?.tagName || '').toLowerCase();
      if (tag === 'input' || tag === 'textarea' || el?.isContentEditable) return;
      if (/^[0-9]$/.test(e.key)) inputDigit(e.key);
      else if (e.key === '.') inputDigit('.');
      else if (e.key === '+') applyOp('+');
      else if (e.key === '-') applyOp('−');
      else if (e.key === '*') applyOp('×');
      else if (e.key === '/') { e.preventDefault(); applyOp('÷'); }
      else if (e.key === '%') applyOp('%');
      else if (e.key === 'Enter' || e.key === '=') { e.preventDefault(); equals(); }
      else if (e.key === 'Backspace') backspace();
      else if (e.key === 'Escape') clear();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const btn = 'h-12 rounded-lg text-lg font-medium transition-colors select-none';
  const num = `${btn} bg-desktop-surface hover:bg-desktop-hover text-desktop-text`;
  const fn = `${btn} bg-desktop-surface2 hover:bg-desktop-hover text-desktop-text/70 text-sm`;
  const oper = `${btn} bg-desktop-accent/20 hover:bg-desktop-accent/30 text-desktop-accent font-semibold`;
  const eq = `${btn} bg-desktop-accent hover:opacity-90 text-white font-semibold`;

  return (
    <div className="h-full flex flex-col bg-desktop-surface2 p-3 gap-2">
      <div className="bg-desktop-surface rounded-xl px-4 py-4 text-right shrink-0 border border-desktop-border">
        <div className="text-[11px] h-4 text-desktop-text/40 truncate">
          {acc !== null && op ? `${acc} ${op}` : '\u00a0'}
        </div>
        <div className="text-3xl font-light text-desktop-text truncate">{display}</div>
      </div>
      <div className="grid grid-cols-4 gap-1.5 flex-1">
        <button className={fn} onClick={clear}>C</button>
        <button className={fn} onClick={backspace} title="Backspace"><Delete className="w-4 h-4 mx-auto" /></button>
        <button className={fn} onClick={() => applyOp('%')}>%</button>
        <button className={oper} onClick={() => applyOp('÷')}>÷</button>
        <button className={num} onClick={() => inputDigit('7')}>7</button>
        <button className={num} onClick={() => inputDigit('8')}>8</button>
        <button className={num} onClick={() => inputDigit('9')}>9</button>
        <button className={oper} onClick={() => applyOp('×')}>×</button>
        <button className={num} onClick={() => inputDigit('4')}>4</button>
        <button className={num} onClick={() => inputDigit('5')}>5</button>
        <button className={num} onClick={() => inputDigit('6')}>6</button>
        <button className={oper} onClick={() => applyOp('−')}>−</button>
        <button className={num} onClick={() => inputDigit('1')}>1</button>
        <button className={num} onClick={() => inputDigit('2')}>2</button>
        <button className={num} onClick={() => inputDigit('3')}>3</button>
        <button className={oper} onClick={() => applyOp('+')}>+</button>
        <button className={num} onClick={negate}>+/−</button>
        <button className={num} onClick={() => inputDigit('0')}>0</button>
        <button className={num} onClick={() => inputDigit('.')}>.</button>
        <button className={eq} onClick={equals}>=</button>
      </div>
    </div>
  );
}
